import { getSinglePromptTemplateOverride } from "./evaluate-drawer-utils";
import { useEvaluateScope } from "./useEvaluateScope";

import { ChooseEntityDialogControlled } from "@/components/ChooseEntityDialog";
import { Entity, EntityType } from "@/queries";
import {
  PlaygroundEvaluateSheetReference,
  useCreateEvaluateSheetReference,
  usePatchPlaygroundEvaluateSettings,
  useUpdateEvaluateSheetReference,
} from "@/queries/playground/evaluate";
import { displayErrorToast } from "@/utils/toast";

type SmartTableEntityWithSheet = Entity<EntityType.SMART_TABLE> & {
  selected_sheet_id?: string;
};

interface ReferenceSheetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingReferenceCount: number;
  reference?: PlaygroundEvaluateSheetReference;
  onReferenceCreated?: (reference: PlaygroundEvaluateSheetReference) => void;
}

const ReferenceSheetDialog = ({
  open,
  onOpenChange,
  existingReferenceCount,
  reference,
  onReferenceCreated,
}: ReferenceSheetDialogProps) => {
  const { queryScope } = useEvaluateScope();
  const createReference = useCreateEvaluateSheetReference(queryScope);
  const updateReference = useUpdateEvaluateSheetReference(queryScope);
  const patchSettings = usePatchPlaygroundEvaluateSettings(queryScope);

  const handleSelect = (entity: Entity<EntityType.SMART_TABLE>) => {
    const selected = entity as SmartTableEntityWithSheet;
    if (!selected.selected_sheet_id) {
      displayErrorToast("Choose a sheet to evaluate.");
      return;
    }
    const nextSheet = {
      smart_table_id: String(selected.id),
      sheet_id: selected.selected_sheet_id,
    };
    if (reference) {
      updateReference.mutate(
        { referenceId: reference.id, body: nextSheet },
        { onSuccess: () => onOpenChange(false) },
      );
      return;
    }

    createReference.mutate(nextSheet, {
      onSuccess: response => {
        const templateOverride = getSinglePromptTemplateOverride(response.sheet_reference.columns);
        if (templateOverride) {
          updateReference.mutate({
            referenceId: response.sheet_reference.id,
            body: { template_override: templateOverride },
          });
        }
        if (onReferenceCreated) {
          onReferenceCreated(response.sheet_reference);
        } else if (existingReferenceCount === 0) {
          patchSettings.mutate({
            active_sheet_reference_id: response.sheet_reference.id,
          });
        }
        onOpenChange(false);
      },
    });
  };

  return (
    <ChooseEntityDialogControlled
      open={open}
      onOpenChange={onOpenChange}
      type={EntityType.SMART_TABLE}
      currentFolderId={null}
      mode="single"
      onSelect={handleSelect}
      config={{ selectSmartTableSheet: true, selectLatest: true }}
    />
  );
};

export default ReferenceSheetDialog;
